// Currency formatting utilities

export const currencySymbols = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  CHF: 'CHF',
  CNY: '¥',
  INR: '₹',
  MXN: '$',
  BRL: 'R$',
  KRW: '₩',
  SGD: 'S$',
  HKD: 'HK$',
  NOK: 'kr',
  SEK: 'kr',
  DKK: 'kr',
  NZD: 'NZ$',
  ZAR: 'R',
  RUB: '₽',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei'
}

export const currencyNames = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  CAD: 'Canadian Dollar',
  AUD: 'Australian Dollar',
  JPY: 'Japanese Yen',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan',
  INR: 'Indian Rupee',
  MXN: 'Mexican Peso',
  BRL: 'Brazilian Real',
  KRW: 'South Korean Won',
  SGD: 'Singapore Dollar',
  HKD: 'Hong Kong Dollar',
  NOK: 'Norwegian Krone',
  SEK: 'Swedish Krona',
  DKK: 'Danish Krone',
  NZD: 'New Zealand Dollar',
  ZAR: 'South African Rand',
  RUB: 'Russian Ruble',
  PLN: 'Polish Złoty',
  CZK: 'Czech Koruna',
  HUF: 'Hungarian Forint',
  RON: 'Romanian Leu'
}

/**
 * Format a price with the appropriate currency symbol
 * @param {number} amount - The price amount
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR')
 * @param {boolean} includeDecimals - Whether to include decimal places (default: true)
 * @param {boolean} symbolAtEnd - Whether to place currency symbol at the end (default: false)
 * @returns {string} - Formatted price string
 */
export function formatPrice(amount, currency = 'USD', includeDecimals = true, symbolAtEnd = false) {
  const symbol = currencySymbols[currency] || '$'
  const numAmount = parseFloat(amount) || 0
  
  let formattedAmount
  if (includeDecimals) {
    formattedAmount = numAmount.toFixed(2)
  } else {
    formattedAmount = Math.round(numAmount).toString()
  }
  
  if (symbolAtEnd) {
    return `${formattedAmount} ${symbol}`
  } else {
    return `${symbol}${formattedAmount}`
  }
}

/**
 * Format a price for public/preview menus with currency symbol at the end
 * @param {number} amount - The price amount
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR')
 * @returns {string} - Formatted price string with symbol at end
 */
export function formatPriceForPublic(amount, currency = 'USD') {
  return formatPrice(amount, currency, true, true)
}

/**
 * Get currency symbol for a given currency code
 * @param {string} currency - The currency code
 * @returns {string} - Currency symbol
 */
export function getCurrencySymbol(currency = 'USD') {
  return currencySymbols[currency] || '$'
}

/**
 * Get currency name for a given currency code
 * @param {string} currency - The currency code
 * @returns {string} - Currency name
 */
export function getCurrencyName(currency = 'USD') {
  return currencyNames[currency] || 'US Dollar'
}

/**
 * Get all available currencies as an array of objects
 * @returns {Array} - Array of currency objects with code, name, and symbol
 */
export function getAllCurrencies() {
  return Object.keys(currencySymbols).map(code => ({
    code,
    name: currencyNames[code],
    symbol: currencySymbols[code]
  }))
}
